
import { Component, Prop, Ref, Vue } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { namespace } from 'vuex-class';

const DynamicVirtualScroller = () =>
	import(
		/* webpackChunkName: "dynamic-virtual-scroller" */ '@/components/virtual-scroller/DynamicVirtualScroller.vue'
	);

import TitleScoreList from '@/components/TitleScoreList.vue';
import BuyBox from '@/components/buybox/BuyBox.vue';
import InfiniteScroll from '@/components/list/InfiniteScroll.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';

import { WatchlistTitleGraphqlFragment } from '@/components/watchlist/graphql/fragments/WatchlistTitle.fragment';
import { PosterGraphqlProps } from '@/interfaces/components/poster/PosterGraphql';
import { Credit } from '@/interfaces/titles';

import { ObjectType, TitleListType } from '@/@types/graphql-types';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';

const App = namespace('app');
const Routing = namespace('routing');

@Component({
	name: 'TitleListRowGraphql',
	components: { BuyBox, DynamicVirtualScroller, PosterGraphql, TitleScoreList, InfiniteScroll, TitleDetailLink },
})
export default class TitleListRowGraphql extends Vue {
	TitleListType = TitleListType;

	@Prop({ default: () => [] }) titles: WatchlistTitleGraphqlFragment[];
	@Prop() isAtEndOfList: boolean;
	@Prop() hasNoTitles: boolean;
	@Prop() loading: boolean;
	@Prop({ default: false }) isSeoSRLoaded: boolean;
	@Prop({ default: true }) virtualScrolling: boolean;

	@Prop({ default: null }) sponsoredAd: SponsoredAdFragment;

	@Ref('infiniteScroll') readonly infiniteScroll?: InfiniteScroll;

	@App.Getter isSeoUser: boolean;

	@Routing.State activeRoute: Route;

	get disableInfiniteScroll() {
		const isTitleDetailPage = this.activeRoute.name?.includes('app.detail');
		return this.hasNoTitles || this.isAtEndOfList || isTitleDetailPage;
	}

	get isRealCampaign() {
		return !!this.sponsoredAd?.campaign && this.sponsoredAd?.campaign?.node.__typename !== 'Season';
	}

	get isSponsoredRecommendationActive() {
		return (this.isRealCampaign || this.isSeoUser) && this.sponsoredAd && !this.sponsoredAd?.holdoutGroup;
	}

	get sponsoredAdPath() {
		type SRNode = AssertByTypename<typeof this.sponsoredAd.campaign.node, 'Movie' | 'Show'>;
		return (this.sponsoredAd?.campaign?.node as SRNode)?.content?.fullPath;
	}

	get sponsoredAdCampaign() {
		return this.sponsoredAd?.campaign;
	}

	get isListSr() {
		return this.sponsoredAd?.campaign?.node?.__typename === 'GenericTitleList';
	}

	// Resets the internal state of the infinite scroll. Needs to be called at each filter change.
	resetInfiniteScroll() {
		this.infiniteScroll?.reset();
	}

	filterCredits(credits: Credit[]): Credit[] {
		if (!credits) {
			return [];
		}

		return credits.filter((credit: Credit): boolean => {
			return !!credit.character_name;
		});
	}

	isTitleInList(listName: TitleListType, title: any) {
		switch (listName) {
			case TitleListType.WatchNext:
				return !!title?.watchlistEntryV2?.createdAt;
			case TitleListType.Likelist:
				return !!title?.likelistEntry?.createdAt;
			case TitleListType.Dislikelist:
				return !!title?.dislikelistEntry?.createdAt;
			case TitleListType.CaughtUp:
				return title.objectType === ObjectType.Movie
					? !!title?.seenlistEntry?.createdAt
					: title?.seenState?.progress === 100;
		}
	}

	posterGraphqlProps(title: WatchlistTitleGraphqlFragment, index: number = 0): PosterGraphqlProps {
		const props: PosterGraphqlProps = {
			titleId: title.node.id,
			titleObjectId: title.node.objectId,
			titleObjectType: title.node.objectType,
			titleName: title.node.content.title,
			titleBackdrops: title.node.content.backdrops,
			titleScoring: title.node.content.scoring,
			isInCustomList: !!title.node.customlistEntries?.length,
			isInWatchlist: this.isTitleInList(TitleListType.WatchNext, title.node),
			isInSeenList: this.isTitleInList(TitleListType.CaughtUp, title.node),
			isInLikeList: this.isTitleInList(TitleListType.Likelist, title.node),
			isInDislikeList: this.isTitleInList(TitleListType.Dislikelist, title.node),
			isTitleReleased: title.node.content.isReleased,
			lazy: BUILD_CONFIG.globals.BUILD_TARGET === 'SSR' ? index >= IMG_POSTER_LOAD_COUNT : true,
		};

		if (title.node.content.posterUrl) {
			props.titlePosterUrl = title.node.content.posterUrl;
		}

		if (title.node.__typename === 'Show') {
			props.seenEpisodeCount = (title.node.seenState || {}).seenEpisodeCount;
			props.isTvShowTracked = !!title.node.tvShowTrackingEntry?.createdAt;
		}

		return props;
	}
}
